import { Button, Stack, VStack, HStack, Heading, Separator, SimpleGrid, Box, Popover, Flex, Input, Portal } from '@chakra-ui/react';
import * as React from 'react';
import React__default, { useState, useMemo, useCallback, Fragment, useRef, useEffect } from 'react';
import { startOfDay, isBefore, isToday, addDays, differenceInCalendarMonths, format, parse } from 'date-fns';

var Month_Names_Full = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
var Month_Names_Short = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
var Weekday_Names_Short = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}
function _objectWithoutPropertiesLoose(r, e) {
  if (null == r) return {};
  var t = {};
  for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
    if (-1 !== e.indexOf(n)) continue;
    t[n] = r[n];
  }
  return t;
}

/*
 * credit:
 * This is a Typescript rewrite of: https://github.com/deseretdigital/dayzed
 */
/**
 * This is intended to be used to compose event handlers
 * They are executed in order until one of them calls
 * `event.preventDefault()`. Not sure this is the best
 * way to do this, but it seems legit...
 * @param {Function} fns the event hanlder functions
 * @return {Function} the event handler to add to an element
 */
function composeEventHandlers() {
  for (var _len = arguments.length, fns = new Array(_len), _key = 0; _key < _len; _key++) {
    fns[_key] = arguments[_key];
  }
  // @ts-ignore
  return function (event) {
    for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
      args[_key2 - 1] = arguments[_key2];
    }
    return fns.some(function (fn) {
      fn && fn.apply(void 0, [event].concat(args));
      return event.defaultPrevented;
    });
  };
}
/**
 * Takes a calendars array and figures out the number of months to subtract
 * based on the current offset and the minDate allowed.
 * @param {Object} param The param object
 * @param {Array} param.calendars The calendars array created by the getCalendars function
 * @param {Number} param.offset The num of months to be subtracted
 * @param {Date} param.minDate The earliest date we are allow to subtract back to
 * @returns {Number} The number of months to subtract
 */
function subtractMonth(_ref) {
  var calendars = _ref.calendars,
    offset = _ref.offset,
    minDate = _ref.minDate;
  if (offset > 1 && minDate) {
    var firstDayOfMonth = calendars[0].firstDayOfMonth;
    var diffInMonths = differenceInCalendarMonths(firstDayOfMonth, minDate);
    if (diffInMonths < offset) {
      offset = diffInMonths;
    }
  }
  return offset;
}
/**
 * Takes a calendars array and figures out the number of months to add
 * based on the current offset and the maxDate allowed.
 * @param {Object} param The param object
 * @param {Array} param.calendars The calendars array created by the getCalendars function
 * @param {Number} param.offset The num of months to be added
 * @param {Date} param.maxDate The furthest date we are allow to add forward to
 * @returns {Number} The number of months to add
 */
function addMonth(_ref2) {
  var calendars = _ref2.calendars,
    offset = _ref2.offset,
    maxDate = _ref2.maxDate;
  if (offset > 1 && maxDate) {
    var lastDayOfMonth = calendars[calendars.length - 1].lastDayOfMonth;
    var diffInMonths = differenceInCalendarMonths(maxDate, lastDayOfMonth);
    if (diffInMonths < offset) {
      offset = diffInMonths;
    }
  }
  return offset;
}
/**
 * Takes a calendars array and figures out if the back button should be
 * disabled based on the minDate allowed.
 * @param {Object} param The param object
 * @param {Array} param.calendars The calendars array created by the getCalendars function
 * @param {Date} param.minDate The earliest date available
 * @returns {Boolean} Whether the back button should be disabled.
 */
function isBackDisabled(_ref3) {
  var calendars = _ref3.calendars,
    minDate = _ref3.minDate;
  if (!minDate) {
    return false;
  }
  var firstDayOfMonth = calendars[0].firstDayOfMonth;
  var firstDayOfMonthMinusOne = addDays(firstDayOfMonth, -1);
  if (isBefore(firstDayOfMonthMinusOne, minDate)) {
    return true;
  }
  return false;
}
/**
 * Takes a calendars array and figures out if the forward button should be
 * disabled based on the maxDate allowed.
 * @param {Object} param The param object
 * @param {Array} param.calendars The calendars array created by the getCalendars function
 * @param {Date} param.maxDate The furthest date available
 * @returns {Boolean} Whether the forward button should be disabled.
 */
function isForwardDisabled(_ref4) {
  var calendars = _ref4.calendars,
    maxDate = _ref4.maxDate;
  if (!maxDate) {
    return false;
  }
  var lastDayOfMonth = calendars[calendars.length - 1].lastDayOfMonth;
  var lastDayOfMonthPlusOne = addDays(lastDayOfMonth, 1);
  if (isBefore(maxDate, lastDayOfMonthPlusOne)) {
    return true;
  }
  return false;
}
/**
 * Figures out the months data needed based off the number of monthsToDisplay
 * and other options provided.
 * @param {Object} param The param object
 * @param {Date} param.date The date to start the calendar at
 * @param {Array.<Date>} param.selected An array of dates currently selected
 * @param {Number} param.monthsToDisplay The number of months to return in the calendar view
 * @param {Number} param.offset The number of months to offset based off the param.date given
 * @param {Date} param.minDate The earliest date available
 * @param {Date} param.maxDate The furthest date available
 * @param {Number} param.firstDayOfWeek First day of week, 0-6 (Sunday to Saturday)
 * @param {Bool} param.showOutsideDays Flag to fill front and back weeks with dates from adjacent months
 * @returns {Array.<Object>} An array of objects with month data
 */
function getCalendars(_ref5) {
  var date = _ref5.date,
    selected = _ref5.selected,
    monthsToDisplay = _ref5.monthsToDisplay,
    offset = _ref5.offset,
    minDate = _ref5.minDate,
    maxDate = _ref5.maxDate,
    firstDayOfWeek = _ref5.firstDayOfWeek,
    showOutsideDays = _ref5.showOutsideDays;
  var months = [];
  var startDate = getStartDate(date, minDate, maxDate);
  for (var i = 0; i < monthsToDisplay; i++) {
    var calendarDates = getMonths({
      month: startDate.getMonth() + i + offset,
      year: startDate.getFullYear(),
      selectedDates: selected,
      minDate: minDate,
      maxDate: maxDate,
      firstDayOfWeek: firstDayOfWeek,
      showOutsideDays: showOutsideDays
    });
    months.push(calendarDates);
  }
  return months;
}
/**
 * Figures out the actual start date based on
 * the min and max dates available.
 * @param {Date} date The we want to start the calendar at
 * @param {Date} minDate The earliest date available to start at
 * @param {Date} maxDate The latest date available to start at
 * @returns {Date} The actual start date
 */
function getStartDate(date, minDate, maxDate) {
  var startDate = startOfDay(date);
  if (minDate) {
    var minDateNormalized = startOfDay(minDate);
    if (isBefore(startDate, minDateNormalized)) {
      startDate = minDateNormalized;
    }
  }
  if (maxDate) {
    var maxDateNormalized = startOfDay(maxDate);
    if (isBefore(maxDateNormalized, startDate)) {
      startDate = maxDateNormalized;
    }
  }
  return startDate;
}
/**
 * Figures what week/day data to return for the given month
 * and year. Adds flags to day data if found in the given selectedDates,
 * if is selectable inside the given min and max dates, or is today.
 * @param {Object} param The param object
 * @param {Number} param.month The month to grab data for
 * @param {Number} param.year The year to grab data for
 * @param {Array.<Date>} param.selectedDates An array of dates currently selected
 * @param {Date} param.minDate The earliest date available
 * @param {Date} param.maxDate The furthest date available
 * @param {Number} param.firstDayOfWeek First day of week, 0-6 (Sunday to Saturday)
 * @param {Bool} param.showOutsideDays Flag to fill front and back weeks with dates from adjacent months
 * @returns {Object} The data for the selected month/year
 */
function getMonths(_ref6) {
  var month = _ref6.month,
    year = _ref6.year,
    selectedDates = _ref6.selectedDates,
    minDate = _ref6.minDate,
    maxDate = _ref6.maxDate,
    firstDayOfWeek = _ref6.firstDayOfWeek,
    showOutsideDays = _ref6.showOutsideDays;
  // Get the normalized month and year, along with days in the month.
  var daysMonthYear = getNumDaysMonthYear(month, year);
  var daysInMonth = daysMonthYear.daysInMonth;
  month = daysMonthYear.month;
  year = daysMonthYear.year;
  // Fill out the dates for the month.
  var dates = [];
  for (var day = 1; day <= daysInMonth; day++) {
    var date = new Date(year, month, day);
    var dateObj = {
      date: date,
      selected: isSelected(selectedDates, date),
      selectable: isSelectable(minDate, maxDate, date),
      today: isToday(date),
      prevMonth: false,
      nextMonth: false
    };
    dates.push(dateObj);
  }
  var firstDayOfMonth = new Date(year, month, 1);
  var lastDayOfMonth = new Date(year, month, daysInMonth);
  var frontWeekBuffer = fillFrontWeek({
    firstDayOfMonth: firstDayOfMonth,
    minDate: minDate,
    maxDate: maxDate,
    selectedDates: selectedDates,
    firstDayOfWeek: firstDayOfWeek,
    showOutsideDays: showOutsideDays
  });
  var backWeekBuffer = fillBackWeek({
    lastDayOfMonth: lastDayOfMonth,
    minDate: minDate,
    maxDate: maxDate,
    selectedDates: selectedDates,
    firstDayOfWeek: firstDayOfWeek,
    showOutsideDays: showOutsideDays
  });
  dates.unshift.apply(dates, frontWeekBuffer);
  dates.push.apply(dates, backWeekBuffer);
  // Get the filled out weeks for the
  // given dates.
  var weeks = getWeeks(dates);
  // return the calendar data.
  return {
    firstDayOfMonth: firstDayOfMonth,
    lastDayOfMonth: lastDayOfMonth,
    month: month,
    year: year,
    weeks: weeks
  };
}
/**
 * Fill front week with either empty buffer or dates from previous month,
 * depending on showOutsideDays flag
 * @param {Object} param The param object
 * @param {Array.<Date>} param.selectedDates An array of dates currently selected
 * @param {Date} param.minDate The earliest date available
 * @param {Date} param.maxDate The furthest date available
 * @param {Date} param.firstDayOfMonth First day of the month
 * @param {Number} param.firstDayOfWeek First day of week, 0-6 (Sunday to Saturday)
 * @param {Bool} param.showOutsideDays Flag to fill front and back weeks with dates from adjacent months
 * @returns {Array.<Date>} Buffer to fill front week
 */
function fillFrontWeek(_ref7) {
  var firstDayOfMonth = _ref7.firstDayOfMonth,
    minDate = _ref7.minDate,
    maxDate = _ref7.maxDate,
    selectedDates = _ref7.selectedDates,
    firstDayOfWeek = _ref7.firstDayOfWeek,
    showOutsideDays = _ref7.showOutsideDays;
  var dates = [];
  var firstDay = (firstDayOfMonth.getDay() + 7 - firstDayOfWeek) % 7;
  if (showOutsideDays) {
    var lastDayOfPrevMonth = addDays(firstDayOfMonth, -1);
    var prevDate = lastDayOfPrevMonth.getDate();
    var prevDateMonth = lastDayOfPrevMonth.getMonth();
    var prevDateYear = lastDayOfPrevMonth.getFullYear();
    // Fill out front week for days from
    // preceding month with dates from previous month.
    var counter = 0;
    while (counter < firstDay) {
      var date = new Date(prevDateYear, prevDateMonth, prevDate - counter);
      var dateObj = {
        date: date,
        selected: isSelected(selectedDates, date),
        selectable: isSelectable(minDate, maxDate, date),
        today: false,
        prevMonth: true,
        nextMonth: false
      };
      dates.unshift(dateObj);
      counter++;
    }
  } else {
    // Fill out front week for days from
    // preceding month with buffer.
    while (firstDay > 0) {
      dates.unshift('');
      firstDay--;
    }
  }
  return dates;
}
/**
 * Fill back weeks with either empty buffer or dates from next month,
 * depending on showOutsideDays flag
 * @param {Object} param The param object
 * @param {Array.<Date>} param.selectedDates An array of dates currently selected
 * @param {Date} param.minDate The earliest date available
 * @param {Date} param.maxDate The furthest date available
 * @param {Date} param.lastDayOfMonth Last day of the month
 * @param {Number} param.firstDayOfWeek First day of week, 0-6 (Sunday to Saturday)
 * @param {Bool} param.showOutsideDays Flag to fill front and back weeks with dates from adjacent months
 * @returns {Array.<Date>} Buffer to fill back week
 */
function fillBackWeek(_ref8) {
  var lastDayOfMonth = _ref8.lastDayOfMonth,
    minDate = _ref8.minDate,
    maxDate = _ref8.maxDate,
    selectedDates = _ref8.selectedDates,
    firstDayOfWeek = _ref8.firstDayOfWeek,
    showOutsideDays = _ref8.showOutsideDays;
  var dates = [];
  var lastDay = (lastDayOfMonth.getDay() + 7 - firstDayOfWeek) % 7;
  if (showOutsideDays) {
    var firstDayOfNextMonth = addDays(lastDayOfMonth, 1);
    var nextDateMonth = firstDayOfNextMonth.getMonth();
    var nextDateYear = firstDayOfNextMonth.getFullYear();
    // Fill out back week for days from
    // following month with dates from next month.
    var counter = 0;
    while (counter < 6 - lastDay) {
      var date = new Date(nextDateYear, nextDateMonth, 1 + counter);
      var dateObj = {
        date: date,
        selected: isSelected(selectedDates, date),
        selectable: isSelectable(minDate, maxDate, date),
        today: false,
        prevMonth: false,
        nextMonth: true
      };
      dates.push(dateObj);
      counter++;
    }
  } else {
    // Fill out back week for days from
    // following month with buffer.
    while (lastDay < 6) {
      dates.push('');
      lastDay++;
    }
  }
  return dates;
}
/**
 * Normalizes month (could be overflow) and year pairs and returns the
 * normalized month and year along with the number of days in the month.
 * @param {Number} month The month to normalize
 * @param {Number} year The year to normalize
 * @returns {Object} The normalized month and year along with the number of days in the month
 */
function getNumDaysMonthYear(month, year) {
  // If a parameter you specify is outside of the expected range for Month or Day,
  // JS Date attempts to update the date information in the Date object accordingly!
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/setMonth
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/setDate
  // Let Date handle the overflow of the month,
  // which should return the normalized month and year.
  var normalizedMonthYear = new Date(year, month, 1);
  month = normalizedMonthYear.getMonth();
  year = normalizedMonthYear.getFullYear();
  // Overflow the date to the next month, then subtract the difference
  // to get the number of days in the previous month.
  // This will also account for leap years!
  var daysInMonth = 32 - new Date(year, month, 32).getDate();
  return {
    daysInMonth: daysInMonth,
    month: month,
    year: year
  };
}
/**
 * Takes an array of dates, and turns them into a multi dimensional
 * array with 7 entries for each week.
 * @param {Array.<Object>} dates An array of dates
 * @returns {Array} The weeks as a multi dimensional array
 */
function getWeeks(dates) {
  var weeksLength = Math.ceil(dates.length / 7);
  var weeks = [];
  for (var i = 0; i < weeksLength; i++) {
    weeks[i] = [];
    for (var x = 0; x < 7; x++) {
      weeks[i].push(dates[i * 7 + x]);
    }
  }
  return weeks;
}
/**
 * Normalizes dates to the beginning of the day,
 * then checks to see if the day given is found
 * in the selectedDates.
 * @param {Array.<Date>} selectedDates An array of dates currently selected
 * @param {Date} date The date to search with against selectedDates
 * @returns {Boolean} Whether day is found in selectedDates
 */
function isSelected(selectedDates, date) {
  selectedDates = Array.isArray(selectedDates) ? selectedDates : [selectedDates];
  return selectedDates.some(function (selectedDate) {
    if (selectedDate instanceof Date && startOfDay(selectedDate).getTime() === startOfDay(date).getTime()) {
      return true;
    }
    return false;
  });
}
/**
 * Checks to see if the date given is
 * between the min and max dates.
 * @param {Date} minDate The earliest date available
 * @param {Date} maxDate The furthest date available
 * @param {Date} date The date to compare with
 * @returns {Boolean} Whether the date is between min and max date
 */
function isSelectable(minDate, maxDate, date) {
  if (minDate && isBefore(date, minDate) || maxDate && isBefore(maxDate, date)) {
    return false;
  }
  return true;
}

var _excluded$2 = ["onClick", "dateObj"],
  _excluded2 = ["onClick", "offset", "calendars"],
  _excluded3 = ["onClick", "offset", "calendars"];
function isOffsetControlled(propOffset) {
  return propOffset !== undefined;
}
function getOffset(propOffset, stateOffset) {
  return isOffsetControlled(propOffset) ? propOffset : stateOffset;
}
var defaultObj = {};
function getDateProps(onDateSelected, // @ts-ignore
_temp) {
  var _ref = _temp === void 0 ? defaultObj : _temp,
    onClick = _ref.onClick,
    dateObj = _ref.dateObj,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$2);
  return _extends({
    // @ts-ignore
    onClick: composeEventHandlers(onClick, function (event) {
      onDateSelected(dateObj, event);
    }),
    disabled: !dateObj.selectable,
    'aria-label': dateObj.date.toDateString(),
    'aria-pressed': dateObj.selected,
    role: 'button'
  }, rest);
}
function getBackProps(_ref2, // @ts-ignore
_temp2) {
  var minDate = _ref2.minDate,
    offsetMonth = _ref2.offsetMonth,
    handleOffsetChanged = _ref2.handleOffsetChanged;
  var _ref3 = _temp2 === void 0 ? {} : _temp2,
    onClick = _ref3.onClick,
    _ref3$offset = _ref3.offset,
    offset = _ref3$offset === void 0 ? 1 : _ref3$offset,
    calendars = _ref3.calendars,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded2);
  return _extends({
    onClick: composeEventHandlers(onClick, function () {
      handleOffsetChanged(offsetMonth - subtractMonth({
        calendars: calendars,
        offset: offset,
        minDate: minDate
      }));
    }),
    disabled: isBackDisabled({
      calendars: calendars,
      minDate: minDate
    }),
    'aria-label': "Go back " + offset + " month" + (offset === 1 ? '' : 's')
  }, rest);
}
function getForwardProps(_ref4, // @ts-ignore
_temp3) {
  var maxDate = _ref4.maxDate,
    offsetMonth = _ref4.offsetMonth,
    handleOffsetChanged = _ref4.handleOffsetChanged;
  var _ref5 = _temp3 === void 0 ? {} : _temp3,
    onClick = _ref5.onClick,
    _ref5$offset = _ref5.offset,
    offset = _ref5$offset === void 0 ? 1 : _ref5$offset,
    calendars = _ref5.calendars,
    rest = _objectWithoutPropertiesLoose(_ref5, _excluded3);
  return _extends({
    onClick: composeEventHandlers(onClick, function () {
      handleOffsetChanged(offsetMonth + addMonth({
        calendars: calendars,
        offset: offset,
        maxDate: maxDate
      }));
    }),
    disabled: isForwardDisabled({
      calendars: calendars,
      maxDate: maxDate
    }),
    'aria-label': "Go forward " + offset + " month" + (offset === 1 ? '' : 's')
  }, rest);
}
function onOffsetChangedDefault() {}
function useDayzed(_ref6) {
  var _ref6$date = _ref6.date,
    date = _ref6$date === void 0 ? new Date() : _ref6$date,
    maxDate = _ref6.maxDate,
    minDate = _ref6.minDate,
    _ref6$monthsToDisplay = _ref6.monthsToDisplay,
    monthsToDisplay = _ref6$monthsToDisplay === void 0 ? 1 : _ref6$monthsToDisplay,
    _ref6$firstDayOfWeek = _ref6.firstDayOfWeek,
    firstDayOfWeek = _ref6$firstDayOfWeek === void 0 ? 0 : _ref6$firstDayOfWeek,
    _ref6$showOutsideDays = _ref6.showOutsideDays,
    showOutsideDays = _ref6$showOutsideDays === void 0 ? false : _ref6$showOutsideDays,
    offset = _ref6.offset,
    onDateSelected = _ref6.onDateSelected,
    _ref6$onOffsetChanged = _ref6.onOffsetChanged,
    onOffsetChanged = _ref6$onOffsetChanged === void 0 ? onOffsetChangedDefault : _ref6$onOffsetChanged,
    selected = _ref6.selected;
  var _useState = useState(0),
    stateOffset = _useState[0],
    setStateOffset = _useState[1];
  var offsetMonth = getOffset(offset, stateOffset);
  var calendars = useMemo(function () {
    return getCalendars({
      date: date,
      selected: selected,
      monthsToDisplay: monthsToDisplay,
      minDate: minDate,
      maxDate: maxDate,
      offset: offsetMonth,
      firstDayOfWeek: firstDayOfWeek,
      showOutsideDays: showOutsideDays
    });
  }, [date, firstDayOfWeek, maxDate, minDate, monthsToDisplay, offsetMonth, selected, showOutsideDays]);
  var handleOffsetChanged = useCallback(function (newOffset) {
    if (!isOffsetControlled(offset)) {
      setStateOffset(newOffset);
    }
    onOffsetChanged(newOffset);
  }, [offset, onOffsetChanged]);
  var getDatePropsMemo = useMemo(function () {
    return getDateProps.bind(null, onDateSelected);
  }, [onDateSelected]);
  var getBackPropsMemo = useMemo(function () {
    return getBackProps.bind(null, {
      minDate: minDate,
      offsetMonth: offsetMonth,
      handleOffsetChanged: handleOffsetChanged
    });
  }, [handleOffsetChanged, minDate, offsetMonth]);
  var getForwardPropsMemo = useMemo(function () {
    return getForwardProps.bind(null, {
      maxDate: maxDate,
      offsetMonth: offsetMonth,
      handleOffsetChanged: handleOffsetChanged
    });
  }, [handleOffsetChanged, maxDate, offsetMonth]);
  return {
    calendars: calendars,
    getDateProps: getDatePropsMemo,
    //@ts-ignore
    getBackProps: getBackPropsMemo,
    //@ts-ignore
    getForwardProps: getForwardPropsMemo
  };
}

// Based on: https://github.com/leon-good-life/arrow-keys-react
// re-implemented by @hexelon in https://github.com/hexelon/chakra-dayzed-datepicker/commit/4f44e565e3975f613b54304d1fdaeb97dd7dfa15
var ArrowKeysReact = /*#__PURE__*/function () {
  function ArrowKeysReact(config) {
    this.config = void 0;
    this.config = config;
  }
  var _proto = ArrowKeysReact.prototype;
  _proto.getEvents = function getEvents() {
    var _this = this;
    return {
      onKeyDown: function onKeyDown(e) {
        switch (e.key) {
          case 'ArrowDown':
            _this.config.down && _this.config.down();
            break;
          case 'ArrowLeft':
            _this.config.left && _this.config.left();
            break;
          case 'ArrowRight':
            _this.config.right && _this.config.right();
            break;
          case 'ArrowUp':
            _this.config.up && _this.config.up();
            break;
        }
      }
    };
  };
  return ArrowKeysReact;
}();

var DefaultBtnStyle = {
  variant: 'ghost',
  size: 'sm'
};
var DatepickerBackBtns = function DatepickerBackBtns(props) {
  var _props$propsConfigs;
  var calendars = props.calendars,
    getBackProps = props.getBackProps;
  var customBtnProps = (_props$propsConfigs = props.propsConfigs) == null ? void 0 : _props$propsConfigs.dateNavBtnProps;
  return React__default.createElement(Fragment, null, React__default.createElement(Button, _extends({}, getBackProps({
    calendars: calendars,
    offset: 12
  }), DefaultBtnStyle, customBtnProps), '<<'), React__default.createElement(Button, _extends({}, getBackProps({
    calendars: calendars
  }), DefaultBtnStyle, customBtnProps), '<'));
};
var DatepickerForwardBtns = function DatepickerForwardBtns(props) {
  var _props$propsConfigs2;
  var calendars = props.calendars,
    getForwardProps = props.getForwardProps;
  var customBtnProps = (_props$propsConfigs2 = props.propsConfigs) == null ? void 0 : _props$propsConfigs2.dateNavBtnProps;
  return React__default.createElement(Fragment, null, React__default.createElement(Button, _extends({}, getForwardProps({
    calendars: calendars
  }), DefaultBtnStyle, customBtnProps), '>'), React__default.createElement(Button, _extends({}, getForwardProps({
    calendars: calendars,
    offset: 12
  }), DefaultBtnStyle, customBtnProps), '>>'));
};

var DayOfMonth = function DayOfMonth(_ref) {
  var dateObj = _ref.dateObj,
    isInRange = _ref.isInRange,
    disabledDates = _ref.disabledDates,
    getDateProps = _ref.getDateProps,
    onMouseEnterHighlight = _ref.onMouseEnterHighlight,
    styleBtnProps = _ref.styleBtnProps;
  var selected = dateObj.selected,
    selectable = dateObj.selectable,
    today = dateObj.today,
    date = dateObj.date;
  var disabled = !selectable || !!(disabledDates != null && disabledDates.has(dateObj.date.getTime()));
  var styleProps = React__default.useMemo(function () {
    var output = _extends({}, styleBtnProps.defaultBtnProps);
    if (isInRange && !disabled) {
      Object.assign(output, styleBtnProps.isInRangeBtnProps);
    }
    if (selected) {
      Object.assign(output, styleBtnProps.selectedBtnProps);
    }
    if (today) {
      Object.assign(output, styleBtnProps.todayBtnProps);
    }
    return output;
  }, [disabled, isInRange, selected, styleBtnProps, today]);
  return React__default.createElement(Button, _extends({}, getDateProps({
    dateObj: dateObj,
    disabled: disabled,
    onMouseEnter: function onMouseEnter() {
      if (onMouseEnterHighlight) {
        onMouseEnterHighlight(dateObj.date);
      }
    }
  }), styleProps), date.getDate());
};

var CalendarPanel = function CalendarPanel(_ref) {
  var _propsConfigs$calenda;
  var dayzedHookProps = _ref.dayzedHookProps,
    configs = _ref.configs,
    propsConfigs = _ref.propsConfigs,
    disabledDates = _ref.disabledDates,
    onMouseEnterHighlight = _ref.onMouseEnterHighlight,
    checkInRange = _ref.checkInRange;
  var _useDayzed = useDayzed(dayzedHookProps),
    calendars = _useDayzed.calendars,
    getBackProps = _useDayzed.getBackProps,
    getForwardProps = _useDayzed.getForwardProps,
    getDateProps = _useDayzed.getDateProps;
  var weekdayNames = React.useMemo(function () {
    var firstDayOfWeek = configs.firstDayOfWeek;
    var dayNames = configs.dayNames;
    if (firstDayOfWeek && firstDayOfWeek > 0) {
      return configs.dayNames.slice(firstDayOfWeek, dayNames.length).concat(dayNames.slice(0, firstDayOfWeek));
    }
    return dayNames;
  }, [configs.firstDayOfWeek, configs.dayNames]);
  // looking for a useRef() approach to replace it
  var getKeyOffset = React.useCallback(function (num) {
    var e = document.activeElement;
    var buttons = document.querySelectorAll('button');
    buttons.forEach(function (el, i) {
      var newNodeKey = i + num;
      if (el === e) {
        if (newNodeKey <= buttons.length - 1 && newNodeKey >= 0) {
          buttons[newNodeKey].focus();
        } else {
          buttons[0].focus();
        }
      }
    });
  }, []);
  var arrowKeysReact = React.useMemo(function () {
    return new ArrowKeysReact({
      left: function left() {
        getKeyOffset(-1);
      },
      right: function right() {
        getKeyOffset(1);
      },
      up: function up() {
        getKeyOffset(-7);
      },
      down: function down() {
        getKeyOffset(7);
      }
    });
  }, [getKeyOffset]);
  var styleBtnProps = React.useMemo(function () {
    var _propsConfigs$dayOfMo, _defaultBtnProps$_hov;
    var _ref2 = (_propsConfigs$dayOfMo = propsConfigs == null ? void 0 : propsConfigs.dayOfMonthBtnProps) != null ? _propsConfigs$dayOfMo : {},
      defaultBtnProps = _ref2.defaultBtnProps,
      isInRangeBtnProps = _ref2.isInRangeBtnProps,
      selectedBtnProps = _ref2.selectedBtnProps,
      todayBtnProps = _ref2.todayBtnProps;
    var halfGap = 0.125; //default Chakra-gap-space-1 is 0.25rem
    return {
      defaultBtnProps: _extends({
        size: 'xs',
        rounded: 'md',
        variant: 'ghost',
        position: 'relative'
      }, defaultBtnProps, {
        _after: _extends({
          content: "''",
          position: 'absolute',
          top: "-" + halfGap + "rem",
          left: "-" + halfGap + "rem",
          bottom: "-" + halfGap + "rem",
          right: "-" + halfGap + "rem",
          borderWidth: halfGap + "rem",
          borderColor: 'transparent'
        }, defaultBtnProps == null ? void 0 : defaultBtnProps._after),
        _hover: _extends({
          bg: 'purple.400'
        }, defaultBtnProps == null ? void 0 : defaultBtnProps._hover, {
          _disabled: _extends({
            bg: 'gray.100'
          }, defaultBtnProps == null || (_defaultBtnProps$_hov = defaultBtnProps._hover) == null ? void 0 : _defaultBtnProps$_hov._disabled)
        })
      }),
      isInRangeBtnProps: _extends({
        background: 'purple.200'
      }, isInRangeBtnProps),
      selectedBtnProps: _extends({
        background: 'purple.200'
      }, selectedBtnProps),
      todayBtnProps: _extends({
        borderColor: 'blue.400'
      }, todayBtnProps)
    };
  }, [propsConfigs == null ? void 0 : propsConfigs.dayOfMonthBtnProps]);
  if (calendars.length <= 0) {
    return null;
  }
  return React.createElement(Stack, _extends({
    className: "datepicker-calendar",
    direction: ['column', 'column', 'row']
  }, propsConfigs == null || (_propsConfigs$calenda = propsConfigs.calendarPanelProps) == null ? void 0 : _propsConfigs$calenda.wrapperProps, arrowKeysReact.getEvents()), calendars.map(function (calendar, calendarIdx) {
    var _propsConfigs$calenda2, _propsConfigs$calenda3, _propsConfigs$calenda4, _propsConfigs$calenda5;
    return React.createElement(VStack, _extends({
      key: calendarIdx,
      height: "100%",
      borderWidth: "1px",
      padding: "0.5rem 0.75rem"
    }, propsConfigs == null || (_propsConfigs$calenda2 = propsConfigs.calendarPanelProps) == null ? void 0 : _propsConfigs$calenda2.contentProps), React.createElement(HStack, _extends({}, propsConfigs == null || (_propsConfigs$calenda3 = propsConfigs.calendarPanelProps) == null ? void 0 : _propsConfigs$calenda3.headerProps), React.createElement(DatepickerBackBtns, {
      calendars: calendars,
      getBackProps: getBackProps,
      propsConfigs: propsConfigs
    }), React.createElement(Heading, _extends({
      size: "sm",
      minWidth: '5rem',
      textAlign: "center"
    }, propsConfigs == null ? void 0 : propsConfigs.dateHeadingProps), configs.monthNames[calendar.month], " ", calendar.year), React.createElement(DatepickerForwardBtns, {
      calendars: calendars,
      getForwardProps: getForwardProps,
      propsConfigs: propsConfigs
    })), React.createElement(Separator, _extends({
      width: 'full'
    }, propsConfigs == null || (_propsConfigs$calenda4 = propsConfigs.calendarPanelProps) == null ? void 0 : _propsConfigs$calenda4.dividerProps)), React.createElement(SimpleGrid, _extends({
      columns: 7,
      gap: 1,
      textAlign: "center"
    }, propsConfigs == null || (_propsConfigs$calenda5 = propsConfigs.calendarPanelProps) == null ? void 0 : _propsConfigs$calenda5.bodyProps), weekdayNames.map(function (day, dayIdx) {
      return React.createElement(Box, _extends({
        fontSize: "sm",
        fontWeight: "semibold",
        key: dayIdx
      }, propsConfigs == null ? void 0 : propsConfigs.weekdayLabelProps), day);
    }), calendar.weeks.map(function (week, weekIdx) {
      return week.map(function (dateObj, index) {
        var key = calendar.month + "-" + calendar.year + "-" + weekIdx + "-" + index;
        if (!dateObj) return React.createElement(Box, {
          key: key
        });
        var isInRange = !!checkInRange && checkInRange(dateObj.date);
        return React.createElement(DayOfMonth, {
          key: key,
          dateObj: dateObj,
          propsConfigs: propsConfigs,
          getDateProps: getDateProps,
          isInRange: isInRange,
          disabledDates: disabledDates,
          onMouseEnterHighlight: onMouseEnterHighlight,
          styleBtnProps: styleBtnProps
        });
      });
    })));
  }));
};

var CalendarIcon = function CalendarIcon() {
  return React__default.createElement("svg", {
    viewBox: "0 0 14 14",
    style: {
      width: 14,
      height: 14
    },
    width: 14,
    height: 14,
    focusable: "false"
  }, React__default.createElement("path", {
    fill: "currentColor",
    d: "M10.8889,5.5 L3.11111,5.5 L3.11111,7.05556 L10.8889,7.05556 L10.8889,5.5 Z M12.4444,1.05556 L11.6667,1.05556 L11.6667,0 L10.1111,0 L10.1111,1.05556 L3.88889,1.05556 L3.88889,0 L2.33333,0 L2.33333,1.05556 L1.55556,1.05556 C0.692222,1.05556 0.00777777,1.75556 0.00777777,2.61111 L0,12.5 C0,13.3556 0.692222,14 1.55556,14 L12.4444,14 C13.3,14 14,13.3556 14,12.5 L14,2.61111 C14,1.75556 13.3,1.05556 12.4444,1.05556 Z M12.4444,12.5 L1.55556,12.5 L1.55556,3.94444 L12.4444,3.94444 L12.4444,12.5 Z M8.55556,8.61111 L3.11111,8.61111 L3.11111,10.1667 L8.55556,10.1667 L8.55556,8.61111 Z"
  }));
};

var _excluded$1 = ["date", "name", "disabled", "onDateChange", "id", "minDate", "maxDate", "configs", "usePortal", "portalRef", "disabledDates", "defaultIsOpen", "closeOnSelect", "children"];
var Default_Trigger_Icon_Btn_Props = {
  position: 'absolute',
  variant: 'ghost',
  right: '0',
  size: 'xs',
  marginRight: '5px',
  rounded: 'md',
  zIndex: 1,
  type: 'button',
  padding: '8px'
};
var Default_Popover_Body_Props = {
  rounded: 'md',
  borderColor: 'border',
  borderWidth: 1,
  paddingX: '3',
  paddingY: '2'
};
var DefaultConfigs$1 = {
  dateFormat: 'yyyy-MM-dd',
  monthNames: Month_Names_Short,
  dayNames: Weekday_Names_Short,
  firstDayOfWeek: 0,
  monthsToDisplay: 1
};
var SingleDatepicker = function SingleDatepicker(_ref) {
  var _restProps$propsConfi, _restProps$triggerVar, _restProps$propsConfi2, _restProps$propsConfi3, _restProps$propsConfi4, _restProps$propsConfi5, _restProps$propsConfi6;
  var selectedDate = _ref.date,
    name = _ref.name,
    disabled = _ref.disabled,
    onDateChange = _ref.onDateChange,
    id = _ref.id,
    minDate = _ref.minDate,
    maxDate = _ref.maxDate,
    configs = _ref.configs,
    _ref$usePortal = _ref.usePortal,
    usePortal = _ref$usePortal === void 0 ? false : _ref$usePortal,
    disabledDates = _ref.disabledDates,
    _ref$defaultIsOpen = _ref.defaultIsOpen,
    defaultIsOpen = _ref$defaultIsOpen === void 0 ? false : _ref$defaultIsOpen,
    _ref$closeOnSelect = _ref.closeOnSelect,
    closeOnSelect = _ref$closeOnSelect === void 0 ? true : _ref$closeOnSelect,
    children = _ref.children,
    restProps = _objectWithoutPropertiesLoose(_ref, _excluded$1);
  var _useState = useState(selectedDate),
    dateInView = _useState[0],
    setDateInView = _useState[1];
  var _useState2 = useState(0),
    offset = _useState2[0],
    setOffset = _useState2[1];
  var internalUpdate = useRef(false);
  var _useState3 = useState(defaultIsOpen),
    open = _useState3[0],
    setOpen = _useState3[1];
  var Icon = useMemo(function () {
    var _restProps$triggerIco;
    return restProps.triggerVariant === 'input' ? (_restProps$triggerIco = restProps.triggerIcon) != null ? _restProps$triggerIco : React__default.createElement(CalendarIcon, null) : null;
  }, [
  // @ts-expect-error
  restProps.triggerIcon, restProps.triggerVariant]);
  var datepickerConfigs = useMemo(function () {
    return _extends({}, DefaultConfigs$1, configs);
  }, [configs]);
  var _useState4 = useState(selectedDate ? format(selectedDate, datepickerConfigs.dateFormat) : ''),
    tempInput = _useState4[0],
    setInputVal = _useState4[1];
  // dayzed utils
  var handleOnDateSelected = useCallback(function (_ref2) {
    var selectable = _ref2.selectable,
      date = _ref2.date;
    if (!selectable) return;
    if (date instanceof Date && !isNaN(date.getTime())) {
      internalUpdate.current = true;
      onDateChange(date);
      setInputVal(date ? format(date, datepickerConfigs.dateFormat) : '');
      if (closeOnSelect) setOpen(false);
      return;
    }
  }, [closeOnSelect, datepickerConfigs.dateFormat, onDateChange]);
  var handleInputChange = useCallback(function (event) {
    internalUpdate.current = true;
    setInputVal(event.target.value);
    var newDate = parse(event.target.value, datepickerConfigs.dateFormat, new Date());
    if (!(newDate instanceof Date && !isNaN(newDate.getTime()))) {
      return;
    }
    var isDisabled = disabledDates == null ? void 0 : disabledDates.has(startOfDay(newDate).getTime());
    if (isDisabled) return;
    onDateChange(newDate);
    setDateInView(newDate);
  }, [datepickerConfigs.dateFormat, disabledDates, onDateChange]);
  useEffect(function () {
    if (internalUpdate.current) {
      internalUpdate.current = false;
      return;
    }
    setInputVal(typeof selectedDate !== 'undefined' ? format(selectedDate, datepickerConfigs.dateFormat) : '');
    setDateInView(selectedDate);
  }, [datepickerConfigs.dateFormat, selectedDate]);
  return React__default.createElement(Popover.Root, _extends({
    ids: {
      trigger: id
    },
    open: open,
    onOpenChange: function onOpenChange(e) {
      if (e.open) {
        setOpen(e.open);
      } else {
        setOpen(e.open);
        setDateInView(selectedDate);
        setOffset(0);
      }
    },
    portalled: usePortal,
    lazyMount: true,
    unmountOnExit: true
  }, (_restProps$propsConfi = restProps.propsConfigs) == null || (_restProps$propsConfi = _restProps$propsConfi.popoverCompProps) == null ? void 0 : _restProps$propsConfi.popoverRootProps), !children && ((_restProps$triggerVar = restProps.triggerVariant) != null ? _restProps$triggerVar : 'default') === 'default' ? React__default.createElement(Popover.Trigger, {
    asChild: true
  }, React__default.createElement(Button, _extends({
    type: "button",
    variant: 'outline',
    lineHeight: 1,
    paddingX: "1rem",
    disabled: disabled,
    fontSize: 'sm',
    rounded: 'md'
  }, (_restProps$propsConfi2 = restProps.propsConfigs) == null ? void 0 : _restProps$propsConfi2.triggerBtnProps), selectedDate ? format(selectedDate, datepickerConfigs.dateFormat) : '')) : null, !children && restProps.triggerVariant === 'input' ? React__default.createElement(Flex, {
    position: "relative",
    alignItems: 'center'
  }, React__default.createElement(Popover.Anchor, null, React__default.createElement(Input, _extends({
    onKeyUp: function onKeyUp(e) {
      if (e.key === ' ' && !open) {
        e.preventDefault();
        setOpen(true);
      }
    },
    autoComplete: "off",
    width: '10rem',
    disabled: disabled,
    name: name,
    value: tempInput,
    onChange: handleInputChange,
    paddingRight: '2.5rem'
  }, (_restProps$propsConfi3 = restProps.propsConfigs) == null ? void 0 : _restProps$propsConfi3.inputProps))), React__default.createElement(Popover.Trigger, {
    asChild: true
  }, React__default.createElement(Button, _extends({}, Default_Trigger_Icon_Btn_Props, {
    disabled: disabled
  }, (_restProps$propsConfi4 = restProps.propsConfigs) == null ? void 0 : _restProps$propsConfi4.triggerIconBtnProps), Icon))) : null, children ? children(selectedDate) : null, React__default.createElement(Portal, null, React__default.createElement(Popover.Positioner, null, React__default.createElement(Popover.Content, _extends({
    width: "100%"
  }, (_restProps$propsConfi5 = restProps.propsConfigs) == null || (_restProps$propsConfi5 = _restProps$propsConfi5.popoverCompProps) == null ? void 0 : _restProps$propsConfi5.popoverContentProps), React__default.createElement(Popover.Arrow, null, React__default.createElement(Popover.ArrowTip, null)), React__default.createElement(Popover.Body, _extends({}, Default_Popover_Body_Props, (_restProps$propsConfi6 = restProps.propsConfigs) == null || (_restProps$propsConfi6 = _restProps$propsConfi6.popoverCompProps) == null ? void 0 : _restProps$propsConfi6.popoverBodyProps), React__default.createElement(CalendarPanel, {
    dayzedHookProps: {
      showOutsideDays: true,
      monthsToDisplay: datepickerConfigs.monthsToDisplay,
      onDateSelected: handleOnDateSelected,
      selected: selectedDate,
      date: dateInView,
      minDate: minDate,
      maxDate: maxDate,
      offset: offset,
      onOffsetChanged: setOffset,
      firstDayOfWeek: datepickerConfigs.firstDayOfWeek
    },
    configs: datepickerConfigs,
    propsConfigs: restProps.propsConfigs,
    disabledDates: disabledDates
  }))))));
};

var _excluded = ["configs", "id", "name", "selectedDates", "minDate", "maxDate", "onDateChange", "disabled", "children", "usePortal", "portalRef", "defaultIsOpen", "closeOnSelect"];
var RangeCalendarPanel = function RangeCalendarPanel(_ref) {
  var dayzedHookProps = _ref.dayzedHookProps,
    configs = _ref.configs,
    propsConfigs = _ref.propsConfigs,
    selected = _ref.selected;
  var _React$useState = React__default.useState(null),
    hoveredDate = _React$useState[0],
    setHoveredDate = _React$useState[1];
  // Calendar level
  var onMouseLeave = function onMouseLeave() {
    setHoveredDate(null);
  };
  // Date level
  var onMouseEnterHighlight = function onMouseEnterHighlight(date) {
    if (!Array.isArray(selected) || !(selected != null && selected.length)) {
      return;
    }
    setHoveredDate(date);
  };
  var checkInRange = function checkInRange(date) {
    if (!Array.isArray(selected) || !(selected != null && selected.length)) {
      return false;
    }
    var firstSelected = selected[0];
    if (selected.length === 2) {
      var secondSelected = selected[1];
      return firstSelected < date && secondSelected > date;
    } else {
      return !!hoveredDate && (firstSelected < date && hoveredDate >= date || date < firstSelected && date >= hoveredDate);
    }
  };
  return React__default.createElement(Flex, {
    onMouseLeave: onMouseLeave
  }, React__default.createElement(CalendarPanel, {
    dayzedHookProps: dayzedHookProps,
    configs: configs,
    propsConfigs: propsConfigs,
    checkInRange: checkInRange,
    onMouseEnterHighlight: onMouseEnterHighlight
  }));
};
var DefaultConfigs = {
  dateFormat: 'MM/dd/yyyy',
  monthNames: Month_Names_Short,
  dayNames: Weekday_Names_Short,
  firstDayOfWeek: 0,
  monthsToDisplay: 2
};
var RangeDatepicker = function RangeDatepicker(_ref2) {
  var _restProps$triggerIco, _restProps$triggerVar, _restProps$propsConfi, _restProps$propsConfi2, _restProps$propsConfi3, _restProps$propsConfi4, _restProps$propsConfi5;
  var configs = _ref2.configs,
    id = _ref2.id,
    name = _ref2.name,
    selectedDates = _ref2.selectedDates,
    minDate = _ref2.minDate,
    maxDate = _ref2.maxDate,
    onDateChange = _ref2.onDateChange,
    disabled = _ref2.disabled,
    children = _ref2.children,
    _ref2$usePortal = _ref2.usePortal,
    usePortal = _ref2$usePortal === void 0 ? false : _ref2$usePortal,
    portalRef = _ref2.portalRef,
    _ref2$defaultIsOpen = _ref2.defaultIsOpen,
    defaultIsOpen = _ref2$defaultIsOpen === void 0 ? false : _ref2$defaultIsOpen,
    _ref2$closeOnSelect = _ref2.closeOnSelect,
    closeOnSelect = _ref2$closeOnSelect === void 0 ? true : _ref2$closeOnSelect,
    restProps = _objectWithoutPropertiesLoose(_ref2, _excluded);
  // chakra popover utils
  var _React$useState2 = React__default.useState(selectedDates[0] || new Date()),
    dateInView = _React$useState2[0],
    setDateInView = _React$useState2[1];
  var _React$useState3 = React__default.useState(0),
    offset = _React$useState3[0],
    setOffset = _React$useState3[1];
  var _React$useState4 = React__default.useState(defaultIsOpen),
    open = _React$useState4[0],
    setOpen = _React$useState4[1];
  var Icon = restProps.triggerVariant === 'input' ? (_restProps$triggerIco = restProps.triggerIcon) != null ? _restProps$triggerIco : React__default.createElement(CalendarIcon, null) : null;
  var datepickerConfigs = _extends({}, DefaultConfigs, configs);
  var handleOnDateSelected = function handleOnDateSelected(_ref3) {
    var selectable = _ref3.selectable,
      date = _ref3.date;
    if (!selectable) {
      return;
    }
    var newDates = [].concat(selectedDates);
    if (selectedDates.length) {
      if (selectedDates.length === 1) {
        var firstTime = selectedDates[0];
        if (firstTime < date) {
          newDates.push(date);
        } else {
          newDates.unshift(date);
        }
        onDateChange(newDates);
        if (closeOnSelect) {
          setOpen(false);
        }
        return;
      }
      if (newDates.length === 2) {
        onDateChange([date]);
        return;
      }
    } else {
      newDates.push(date);
      onDateChange(newDates);
    }
  };
  // eventually we want to allow user to freely type their own input and parse the input
  var intVal = selectedDates[0] ? "" + format(selectedDates[0], datepickerConfigs.dateFormat) : "" + datepickerConfigs.dateFormat;
  intVal += selectedDates[1] ? " - " + format(selectedDates[1], datepickerConfigs.dateFormat) : " - " + datepickerConfigs.dateFormat;
  return React__default.createElement(Popover.Root, {
    ids: {
      trigger: id
    },
    open: open,
    onOpenChange: function onOpenChange(e) {
      if (e.open) {
        setOpen(e.open);
      } else {
        setOpen(e.open);
        setDateInView(selectedDates[0] || new Date());
        setOffset(0);
      }
    },
    lazyMount: true,
    unmountOnExit: true
  }, !children && ((_restProps$triggerVar = restProps.triggerVariant) != null ? _restProps$triggerVar : 'default') === 'default' ? React__default.createElement(Popover.Trigger, {
    asChild: true
  }, React__default.createElement(Button, _extends({
    type: "button",
    variant: 'outline',
    lineHeight: 1,
    paddingX: "1rem",
    disabled: disabled,
    fontSize: 'sm',
    rounded: 'md'
  }, (_restProps$propsConfi = restProps.propsConfigs) == null ? void 0 : _restProps$propsConfi.triggerBtnProps), intVal)) : null, !children && restProps.triggerVariant === 'input' ? React__default.createElement(Flex, {
    position: "relative",
    alignItems: 'center'
  }, React__default.createElement(Popover.Anchor, null, React__default.createElement(Input, _extends({
    onKeyUp: function onKeyUp(e) {
      if (e.key === ' ' && !open) {
        e.preventDefault();
        setOpen(true);
      }
    },
    autoComplete: "off",
    width: '16rem',
    paddingRight: '2.5rem',
    disabled: disabled,
    name: name,
    value: intVal,
    onChange: function onChange(e) {
      return e.target.value;
    }
  }, (_restProps$propsConfi2 = restProps.propsConfigs) == null ? void 0 : _restProps$propsConfi2.inputProps))), React__default.createElement(Popover.Trigger, {
    asChild: true
  }, React__default.createElement(Button, _extends({}, Default_Trigger_Icon_Btn_Props, {
    disabled: disabled
  }, (_restProps$propsConfi3 = restProps.propsConfigs) == null ? void 0 : _restProps$propsConfi3.triggerIconBtnProps), Icon))) : null, children ? children(selectedDates) : null, React__default.createElement(Portal, {
    disabled: !usePortal,
    container: portalRef
  }, React__default.createElement(Popover.Positioner, null, React__default.createElement(Popover.Content, _extends({
    width: "100%"
  }, (_restProps$propsConfi4 = restProps.propsConfigs) == null || (_restProps$propsConfi4 = _restProps$propsConfi4.popoverCompProps) == null ? void 0 : _restProps$propsConfi4.popoverContentProps), React__default.createElement(Popover.Body, _extends({}, Default_Popover_Body_Props, (_restProps$propsConfi5 = restProps.propsConfigs) == null || (_restProps$propsConfi5 = _restProps$propsConfi5.popoverCompProps) == null ? void 0 : _restProps$propsConfi5.popoverBodyProps), React__default.createElement(Popover.Arrow, null, React__default.createElement(Popover.ArrowTip, null)), React__default.createElement(RangeCalendarPanel, {
    dayzedHookProps: {
      onDateSelected: handleOnDateSelected,
      selected: selectedDates,
      monthsToDisplay: datepickerConfigs.monthsToDisplay,
      date: dateInView,
      minDate: minDate,
      maxDate: maxDate,
      offset: offset,
      onOffsetChanged: setOffset,
      firstDayOfWeek: datepickerConfigs.firstDayOfWeek
    },
    configs: datepickerConfigs,
    propsConfigs: restProps.propsConfigs,
    selected: selectedDates
  }))))));
};

export { CalendarPanel, Default_Popover_Body_Props, Default_Trigger_Icon_Btn_Props, Month_Names_Full, Month_Names_Short, RangeCalendarPanel, RangeDatepicker, SingleDatepicker, Weekday_Names_Short };
//# sourceMappingURL=chakra-dayzed-datepicker.esm.js.map
